import React, { useState, useRef, useEffect } from "react"
import { Button, Modal } from "antd"
import { CREDIT_ALLOWANCE, CREDITS_REMAINING } from "./aiTools"
import { creditPackages, purchaseCredits, creditPurchaseHistory, confirmCreditPurchase } from './creditPurchaseApi'
import logo from '../../assets/morphosys-logo.png'

export default function StandardCredits({ balance, warning, onDismiss }) {
  const [purchaseOpen, setPurchaseOpen] = useState(false)
  const [catalogue, setCatalogue] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [history, setHistory] = useState({ purchases: [], next: null })
  const requests = useRef({})
  const returned = useRef(false)
  const loadHistory = async (before = null) => {
    setBusy(true); setError('')
    try {
      const next = await creditPurchaseHistory(before)
      setHistory(previous => ({ ...next, purchases: before ? [...previous.purchases, ...next.purchases] : next.purchases }))
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (returned.current || !params.has('credit_purchase')) return
    returned.current = true
    setHistoryOpen(true)
    const reference = params.get('credit_reference')
    const reconcile = async () => {
      if (reference) {
        try { await confirmCreditPurchase(reference) } catch (e) { setError(e.message); return }
      }
      await loadHistory()
    }
    reconcile()
    params.delete('credit_purchase'); params.delete('credit_reference')
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${params.size ? `?${params}` : ''}${window.location.hash}`)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const trial = balance?.mode === "recorded-only-trial"
  const verified = balance?.realBalance && Number.isFinite(balance.monthlyRemaining)
  const remaining = verified ? balance.remaining : trial ? CREDITS_REMAINING : null
  const allowance = balance?.allowance || CREDIT_ALLOWANCE
  const percent = remaining === null ? 0 : Math.max(0, Math.min(100, remaining / allowance * 100))
  const buy = async () => {
    onDismiss(); setPurchaseOpen(true); setError(''); setBusy(true)
    try { setCatalogue(await creditPackages()) } catch (e) { setError(e.message) } finally { setBusy(false) }
  }
  const purchase = async amount => {
    setBusy(true); setError('')
    if (!requests.current[amount]) requests.current[amount] = window.crypto.randomUUID()
    try {
      const data = await purchaseCredits(amount, requests.current[amount])
      const url = new URL(data.url)
      if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com') throw new Error('Unexpected payment address.')
      window.location.assign(url.href)
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }
  return <div className="w-full sm:w-[320px]">
    <div className="flex flex-wrap items-baseline justify-between gap-2 text-[13px]">
      <span className="font-bold">{remaining === null ? "Credit balance unavailable" : `${remaining} / ${allowance} credits remaining`}</span>
      {remaining !== null && <span className="dashboard-muted">{Math.max(0, allowance - remaining)} credits used</span>}
    </div>
    <div role="progressbar" aria-label="AI credits remaining" aria-valuemin={0} aria-valuemax={allowance} aria-valuenow={remaining ?? undefined}
      className="mt-2 h-2 w-full rounded-full overflow-hidden" style={{ background: "var(--dashboard-border)" }}>
      <div className="h-full rounded-full" style={{ width: `${percent}%`, background: "var(--dashboard-primary)" }} />
    </div>
    {trial && <p className="dashboard-muted text-[12px] mt-1">Trial · credits are not deducted</p>}
    {verified && <p className="dashboard-muted text-[12px] mt-1">{balance.mode === 'public-trial' ? `Trial: ${balance.trialRemaining} · Four storyboard images maximum` : `Monthly: ${balance.monthlyRemaining} · Purchased: ${balance.purchasedRemaining}`}</p>}
    <Button size="small" className="dashboard-tool mt-2" disabled={balance?.deductionsEnabled === false || balance?.mode === 'public-trial'} onClick={buy}>Buy Extra Credits</Button>
    {balance?.purchasesAvailable && <Button size="small" type="link" onClick={() => { setHistoryOpen(true); loadHistory() }}>Purchase History</Button>}
    <Modal title="Credit purchase history" open={historyOpen} onCancel={() => setHistoryOpen(false)} width={560}
      footer={<Button onClick={() => setHistoryOpen(false)}>Close</Button>}>
      {error && <p role="alert">{error}</p>}
      {!busy && !history.purchases.length && !error && <p>No credit purchases yet.</p>}
      <ul className="space-y-3">{history.purchases.map(p => <li key={p.reference} className="border-b py-3 flex flex-wrap justify-between gap-2">
        <span>{new Date(p.date).toLocaleDateString()} · {p.credits} credits</span>
        <span>{p.currency === 'aud' ? 'A$' : 'US$'}{(p.amount / 100).toFixed(2)} · {p.state === 'paid' ? 'Paid · credits added' : p.state === 'failed' ? 'Payment failed' : p.state === 'expired' ? 'Expired' : 'Pending confirmation'}</span>
        {p.state === 'pending' && <Button size="small" disabled={busy} onClick={async () => { setBusy(true); try { await confirmCreditPurchase(p.reference); await loadHistory() } catch (e) { setError(e.message) } finally { setBusy(false) } }}>Check payment</Button>}
      </li>)}</ul>
      {busy && <p>Loading…</p>}
      {history.next && <Button disabled={busy} onClick={() => loadHistory(history.next)}>Load more</Button>}
    </Modal>
    <Modal title="You're running low on AI credits" open={Number.isFinite(warning)} onCancel={onDismiss} width={420}
      footer={<div className="flex flex-wrap justify-end gap-2"><Button onClick={onDismiss}>Maybe Later</Button><Button type="primary" onClick={buy}>Buy AI Credits</Button></div>}>
      <p>You have only {warning} credits remaining.</p>
    </Modal>
    <Modal title="Buy AI credits" open={purchaseOpen} onCancel={() => setPurchaseOpen(false)} width={420}
      footer={<Button type="primary" onClick={() => setPurchaseOpen(false)}>OK</Button>}>
      <div className="bg-black w-fit p-3 rounded mb-4"><img src={logo} alt="Morphosys" className="h-12 max-w-full object-contain" /></div>
      {busy && <p>Loading…</p>}
      {error && <p role="alert">{error}</p>}
      {catalogue?.packages?.map(pack => <Button key={pack.amount} className="m-1" disabled={busy} onClick={() => purchase(pack.amount)}>{catalogue.currency === 'aud' ? 'A$' : 'US$'}{pack.amount / 100} · {pack.credits} credits</Button>)}
    </Modal>
  </div>
}